import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { UserApi } from '@shared-features/services/user-api.service';
import { BidChatService, BidChatThreadController, BidChatThreadState, headerFromTarget } from '@features/bid-chat/services/bid-chat.service';
import { BidChatHeader, BidChatMessage, BidChatTarget, ChatMessageType, ChatOfferStatus } from '@features/bid-chat/models/bid-chat.model';
import { BID_CHAT_LIMITS, CHAT_BID_STATUS, CUSTOMER_CHAT_SIDE } from '@features/bid-chat/constants/bid-chat.constants';

/** SAR added or removed by one press of the offer stepper. */
const OFFER_STEP = 50;
/** Within this many px of the bottom, a new message keeps the list pinned to it. */
const STICK_TO_BOTTOM_PX = 80;

type FeedItem =
  | { kind: 'day'; key: string; label: string }
  /** isOwn: the customer's side (right) · isMine: sent by this user, not a colleague of the same company. */
  | { kind: 'message'; key: string; message: BidChatMessage; isOwn: boolean; isMine: boolean };

/** One negotiation thread in the chat drawer (#47189), per the "Chat with fleet" design. */
@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'flex column pt-6' },
})
export class ChatThreadComponent extends BaseComponent implements OnChanges, OnDestroy, AfterViewChecked {
  @Input() target!: BidChatTarget;
  /** Opened from Chat logs: shows the back arrow (a thread opened from a bid has none). */
  @Input() showBack = false;

  @Output() back = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('messagesScroll') messagesScroll?: ElementRef<HTMLElement>;

  state?: BidChatThreadState;
  feed: FeedItem[] = [];
  messageText = '';
  offerAmount = 0;

  readonly offerStep = OFFER_STEP;
  readonly messageType = ChatMessageType;
  readonly offerStatus = ChatOfferStatus;
  readonly bodyMaxLength = BID_CHAT_LIMITS.BODY_MAX_LENGTH;

  private controller?: BidChatThreadController;
  private stateSubscription?: Subscription;
  private renderedNewestId: string | null = null;
  /** Measured before each render: was the reader at the bottom, or scrolled up to older messages? */
  private wasAtBottom = true;

  constructor(
    private bidChatService: BidChatService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private userApi: UserApi
  ) {
    super();
  }

  /** Name, badge and original bid: from the conversation doc, else from the bid. */
  get header(): BidChatHeader {
    return this.state?.header ?? headerFromTarget(this.target);
  }

  /** The name as text; "--" when neither the doc nor the bid has a fleet or driver name. */
  get counterpartName(): string {
    return this.header.counterpartName || '--';
  }

  get canType(): boolean {
    return this.state?.phase === 'ready' && this.state.isOpen;
  }

  /** The composer shows (disabled) while loading; the "can't send" line only once the chat is known closed. */
  get showComposer(): boolean {
    return this.state?.phase !== 'ready' || this.state.isOpen;
  }

  /** Sends queue up, so typing never waits for the previous message to land. */
  get canSendMessage(): boolean {
    return this.canType && !!this.messageText.trim();
  }

  /**
   * The price row: gone once an offer was accepted (in the thread, or the bid itself is ACCEPTED, e.g.
   * from the bids list); text messages go on.
   */
  get canOffer(): boolean {
    if (!this.canType || this.state.bidStatus === CHAT_BID_STATUS.Accepted) return false;
    // An accept in flight (or just done) settles the price: no new offer may race it.
    if (this.state.acceptStatus === 'accepting' || this.state.acceptStatus === 'accepted') return false;
    return !this.state.messages.some((message) => message.type === ChatMessageType.Offer && message.offerStatus === ChatOfferStatus.Accepted);
  }

  /** AC: Send Offer stays disabled while the amount is not a positive number. */
  get canSendOffer(): boolean {
    return this.canOffer && Number(this.offerAmount) > 0;
  }

  /** Skeleton bubbles while the thread or its first messages load. */
  get isLoading(): boolean {
    return this.state?.phase === 'resolving' || (this.state?.phase === 'ready' && !this.state.messagesLoaded);
  }

  /** The offer card's title, from its `offerStatus` (unknown values already read as Deactivated). */
  offerTitleKey(item: FeedItem): string {
    if (item.kind !== 'message') return '';
    if (this.isFailedOffer(item)) return 'bidChat.OfferNotSent';
    switch (item.message.offerStatus) {
      case ChatOfferStatus.Active:
        return item.isOwn ? 'bidChat.CounterOfferSent' : 'bidChat.NewOfferReceived';
      case ChatOfferStatus.Accepted:
        return 'bidChat.OfferAccepted';
      case ChatOfferStatus.Rejected:
        return 'bidChat.OfferRejected';
      default:
        return 'bidChat.OfferDeactivated';
    }
  }

  /** Deactivated and rejected offers are over: the card greys out, per design. */
  isClosedOffer(item: FeedItem): boolean {
    if (item.kind !== 'message') return false;
    const { offerStatus } = item.message;
    return offerStatus === ChatOfferStatus.Deactivated || offerStatus === ChatOfferStatus.Rejected || this.isFailedOffer(item);
  }

  /** My offer the server rejected: it never reached the table, so it is neither "sent" nor waiting. */
  isFailedOffer(item: FeedItem): boolean {
    return item.kind === 'message' && item.isMine && item.message.delivery === 'failed';
  }

  /** Title color per status, per design. */
  offerTitleClass(item: FeedItem): string {
    if (item.kind !== 'message') return '';
    if (this.isFailedOffer(item)) return 'text-warn';
    switch (item.message.offerStatus) {
      case ChatOfferStatus.Active:
        return item.isOwn ? 'text-success' : 'text-secondry';
      case ChatOfferStatus.Accepted:
        return 'text-success';
      case ChatOfferStatus.Rejected:
        return 'text-warn';
      default:
        return 'text-400';
    }
  }

  isActiveOffer(item: FeedItem): boolean {
    return item.kind === 'message' && item.message.offerStatus === ChatOfferStatus.Active && !this.isFailedOffer(item);
  }

  /** Accept shows on the fleet's ACTIVE offer while offers are still possible (same rule as the price row). */
  canAccept(item: FeedItem): boolean {
    return item.kind === 'message' && !item.isOwn && this.isActiveOffer(item) && item.message.delivery !== 'sending' && this.canOffer;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.target) return;

    this.teardown();
    this.messageText = '';
    this.offerAmount = 0;
    this.renderedNewestId = null;

    this.controller = this.bidChatService.openThread(this.target);
    this.stateSubscription = this.controller.state$.subscribe((state) => {
      this.wasAtBottom = this.isAtBottom();
      this.state = state;
      this.feed = this.buildFeed(state.messages);
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * New message: scroll to it on first load, for my own send, or when the reader was already at the
   * bottom. Someone scrolled up to older messages stays where they are.
   */
  ngAfterViewChecked(): void {
    const element = this.messagesScroll?.nativeElement;
    const messages = this.state?.messages ?? [];
    if (!element || !messages.length) return;

    const newest = messages[messages.length - 1];
    if (newest.id !== this.renderedNewestId) {
      const isFirstLoad = this.renderedNewestId === null;
      const isMySend = newest.senderType === CUSTOMER_CHAT_SIDE.senderType && newest.delivery !== 'sent';
      if (isFirstLoad || isMySend || this.wasAtBottom) {
        element.scrollTop = element.scrollHeight;
      }
      this.renderedNewestId = newest.id;
    }
  }

  override ngOnDestroy(): void {
    this.teardown();
    super.ngOnDestroy();
  }

  trackByFeedKey(_index: number, item: FeedItem): string {
    return item.key;
  }

  retry(): void {
    this.controller?.retry();
  }

  stepOffer(direction: 1 | -1): void {
    const current = Number(this.offerAmount) || 0;
    this.offerAmount = Math.max(0, current + direction * OFFER_STEP);
  }

  /** The price goes out as an OFFER message at once (shown as "sending"); the stepper resets. */
  sendOffer(): void {
    if (!this.canSendOffer) return;
    this.controller.sendOffer(Number(this.offerAmount));
    this.offerAmount = 0;
  }

  /** Retry for a message that failed: same id, so it can never arrive twice. */
  resend(message: BidChatMessage): void {
    this.controller?.resend(message.id);
  }

  acceptOffer(offer: BidChatMessage): void {
    this.controller?.acceptOffer(offer).catch(() => undefined);
  }

  /** Shows the message at once as "sending" and clears the input; its state then follows the write. */
  sendMessage(): void {
    if (!this.canSendMessage) return;
    const text = this.messageText;
    this.messageText = '';
    this.controller.send(text);
  }

  /** Enter sends; Shift+Enter keeps the line break. IME composition must not send half a word. */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /** Days are grouped by the LOCAL calendar day, not the UTC one (contract §7.2). */
  private buildFeed(messages: BidChatMessage[]): FeedItem[] {
    const feed: FeedItem[] = [];
    const myUserId = String(this.userApi.currentUser$.value?.id ?? '');
    let previousDay = '';
    messages.forEach((message) => {
      const date = new Date(message.sentAt);
      const day = date.toDateString();
      if (day !== previousDay) {
        previousDay = day;
        feed.push({ kind: 'day', key: `day-${day}`, label: this.dayLabel(date) });
      }
      feed.push({
        kind: 'message',
        key: message.id,
        message,
        isOwn: message.senderType === CUSTOMER_CHAT_SIDE.senderType,
        // This browser's sends not yet in Firestore have no senderUserId: only those count as mine, not
        // any customer-side message without one (e.g. written by the backend).
        isMine:
          message.senderType === CUSTOMER_CHAT_SIDE.senderType &&
          (message.senderUserId ? message.senderUserId === myUserId : message.delivery !== 'sent'),
      });
    });
    return feed;
  }

  private isAtBottom(): boolean {
    const element = this.messagesScroll?.nativeElement;
    return !element || element.scrollHeight - element.scrollTop - element.clientHeight <= STICK_TO_BOTTOM_PX;
  }

  private dayLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return this.translate('bidChat.Today');
    if (date.toDateString() === yesterday.toDateString()) return this.translate('bidChat.Yesterday');
    return this.datePipe.transform(date, 'EEE, d MMM') ?? '';
  }

  private teardown(): void {
    this.stateSubscription?.unsubscribe();
    this.controller?.destroy();
    this.controller = undefined;
  }
}
